import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getExchangeMall } from '@/api';
import type { ExchangeMallProduct } from '@/api';

//卡片文本区估算高度（标题+代金券价格+原价值），用于两列分配
const TEXT_BLOCK_HEIGHT = 96;

//带图片宽高比的商品项（未加载前默认正方形）
interface MallItem extends ExchangeMallProduct {
  ratio: number; //高/宽
}

export default function VoucherMallPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<MallItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const finishedRef = useRef(false);

  /** 追加加载一页 */
  const loadMore = useCallback(async () => {
    if (loadingRef.current || finishedRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const res = await getExchangeMall(pageRef.current, 10);
      setItems((prev) => {
        const seen = new Set(prev.map((it) => it.id));
        const appended = res.list
          .filter((it) => !seen.has(it.id))
          .map((it) => ({ ...it, ratio: 1 }));
        return [...prev, ...appended];
      });
      if ((res.list || []).length < 10) finishedRef.current = true;
      pageRef.current += 1;
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  //滚动到底部自动加载下一页
  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 300) loadMore();
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [loadMore]);

  /** 图片加载完成后记录真实宽高比并重排列 */
  const handleImageLoad = (id: number, ratio: number) => {
    setItems((prev) => prev.map((it) => (it.id === id && it.ratio !== ratio ? { ...it, ratio } : it)));
  };

  //瀑布流：每次放入当前较短的列
  const columns = useMemo(() => {
    const cols: MallItem[][] = [[], []];
    const heights = [0, 0];
    for (const item of items) {
      const h = item.ratio * 100 + TEXT_BLOCK_HEIGHT; //按列宽百分比折算
      const idx = heights[0] <= heights[1] ? 0 : 1;
      cols[idx].push(item);
      heights[idx] += h;
    }
    return cols;
  }, [items]);

  const renderCard = (item: MallItem) => (
    <div
      key={item.id}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border border-background-200/40 cursor-pointer active:scale-[0.98] transition-transform"
      onClick={() => navigate(`/exchange-detail/${item.id}`)}
    >
      {/* 图片区（高度随图片比例自适应） */}
      <div className="relative bg-background-100">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              if (naturalWidth > 0) handleImageLoad(item.id, naturalHeight / naturalWidth);
            }}
            className="w-full block object-cover"
          />
        ) : (
          <div className="w-full aspect-square flex items-center justify-center">
            <i className="ri-image-line text-foreground-300 text-2xl" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] rounded-full font-medium">
          兑换
        </span>
      </div>
      {/* 文本区 */}
      <div className="p-3">
        <h4 className="text-foreground-900 text-sm font-medium line-clamp-2 min-h-[40px]">
          {item.name}
        </h4>
        <div className="flex items-center gap-1 mt-1.5">
          <i className="ri-coins-line text-yellow-500 text-sm" />
          <span className="text-primary-500 text-sm font-bold">
            {Number(item.points).toLocaleString()}
          </span>
          <span className="text-foreground-400 text-[10px]">代金券</span>
        </div>
        <p className="text-foreground-300 text-xs mt-0.5">
          价值 ¥{Number(item.originalPrice).toLocaleString()}
        </p>
      </div>
    </div>
  );

  return (
    <div className="mobile-container bg-background-50 min-h-screen pb-10">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-background-200/50">
        <div className="flex items-center px-4 h-12">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-foreground-700 cursor-pointer hover:bg-background-100 active:scale-95 transition-all"
            aria-label="返回"
          >
            <i className="ri-arrow-left-line text-xl" />
          </button>
          <h1 className="flex-1 text-center text-foreground-950 text-base font-bold tracking-wide">
            代金券兑换商城
          </h1>
          <div className="w-9" />
        </div>
      </header>

      {/* Waterfall list */}
      <main className="px-3 pt-3">
        {error && items.length === 0 ? (
          <div className="text-center py-20">
            <i className="ri-wifi-off-line text-foreground-300 text-3xl mb-3 block" />
            <p className="text-foreground-400 text-sm">{error}</p>
          </div>
        ) : !error && items.length === 0 && loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
            <p className="text-foreground-400 text-sm">加载中...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-gift-line text-foreground-300 text-2xl" />
            </div>
            <p className="text-foreground-400 text-sm font-medium">暂无可兑换商品</p>
            <p className="text-foreground-300 text-xs mt-1">上新的商品会第一时间展示在这里</p>
          </div>
        ) : (
          <div className="flex gap-3 items-start">
            {columns.map((col, idx) => (
              <div key={idx} className="flex-1 min-w-0 space-y-3">
                {col.map(renderCard)}
              </div>
            ))}
          </div>
        )}

        {/* 底部加载状态 */}
        {items.length > 0 && (
          <div className="flex items-center justify-center py-5">
            {loading ? (
              <i className="ri-loader-4-line text-primary-400 text-xl animate-spin" />
            ) : finishedRef.current ? (
              <span className="text-foreground-300 text-xs">没有更多了</span>
            ) : (
              <span className="text-foreground-300 text-xs">上拉加载更多</span>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
